import React from 'react';
import { useQuery } from 'react-query';
import { useParams } from 'react-router';

import dayjs from 'dayjs';
import styled from 'styled-components';

import { getOccupancies } from '../../../../apis/CommunityAPI';
import { useSnapshotContext } from '../../../../common/Snapshot/SnapshotContext';
import { getOccupancyPercentageAggregate } from '../../../../utils/data/occupancies';
import { useCareTypesContext } from '../CareTypesContext';

const Container = styled.div`
  display: inline-flex;
  flex-direction: column;
  align-items: flex-end;
  width: fit-content;
  font-size: 14px;
  color: var(--text-secondary);
  margin-right: 16px;
  row-gap: 0;
`;

const OccupancyRow = styled.div`
  display: flex;
  column-gap: 5px;
`;

const Segment = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 3px;
`;

const Value = styled.span`
  font-size: 20px;
  color: var(--text-primary);
`;

const Percentage = styled.span`
  color: var(--text-primary);
`;

const Text = styled.span`
  color: var(--text-secondary);
  padding-right: 10px;
`;

const LastText = styled(Text)`
  padding-right: 0;
`;

const UpdatedText = styled(Text)`
  padding-right: 0;
  line-height: 1;
  font-size: 12px;
`;

const AccommodationOccupancy = () => {
  const { snapshot } = useSnapshotContext();
  const { careType } = useCareTypesContext();
  const { competitorId } = useParams();
  const { communityId } = useParams();
  const id = Number(competitorId || communityId);

  const { data, isLoading } = useQuery({
    queryKey: ['community', 'occupancy', id, careType],
    queryFn: () => getOccupancies(id, { snapshot: snapshot?.uuid, living_type: [careType?.id] }),
    enabled: Boolean(id),
  });

  const hasData = data?.length > 0;

  if (!hasData || isLoading) {
    return null;
  }

  const { totalUnits, freeUnits, occupancy, hideUnitsRatio } = getOccupancyPercentageAggregate(data);
  const updatedLabel = data[0]?.snapshot_date ? `Updated ${dayjs(data[0]?.snapshot_date).format('MMMM YYYY')}` : null;
  
  return (
    <Container>
      <OccupancyRow>
        {!hideUnitsRatio && (
          <Segment data-test-id="cd-accommodation-occupancy-units">
            <Value>{totalUnits - freeUnits}</Value>
            <span>/</span>
            <Value>{totalUnits}</Value>
            <Text>units occupied </Text>
          </Segment>
        )}
        <Segment data-test-id="cd-accommodation-occupancy-value">
          <Value>{occupancy.toFixed(0)}</Value>
          <Percentage>%</Percentage>
          <LastText>occupancy</LastText>
        </Segment>
      </OccupancyRow>
      {updatedLabel && <UpdatedText>{updatedLabel}</UpdatedText>}
    </Container>
  );
};

export default AccommodationOccupancy;
